import { Activity, ChevronDown, ChevronUp, History, LayoutDashboard, LogOut, Monitor, Moon, Plus, Radar, Siren, Sun } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ApiError, ROLE_LABEL, api, type AddableKind, type CableTier, type LngLat, type SearchResult, type User } from './api'
import { AddCablePanel, AddNodePanel } from './components/AddPanels'
import { ImportPanel } from './components/ImportPanel'
import { AuditPanel } from './components/AuditPanel'
import { CablePanel } from './components/CablePanel'
import { ImpactPanel } from './components/ImpactPanel'
import { IncidentBanner, IncidentPanel, IncidentsPanel } from './components/IncidentPanels'
import { Legend } from './components/Legend'
import { Login } from './components/Login'
import { MapView, type MapDraft, type MapHighlight, type MapInteraction, type Selection } from './components/MapView'
import { NodePanel } from './components/NodePanel'
import { OtdrPanel } from './components/OtdrPanel'
import { PortPathPanel } from './components/PortPathPanel'
import { Overview } from './components/Overview'
import { QualityPanel } from './components/QualityPanel'
import { SearchBox } from './components/SearchBox'
import { TracePanel } from './components/TracePanel'
import { ErrorState, Loading, cx } from './components/ui'
import { Ctx, type AppCtx, type EditState, type View } from './context'
import { useAsync, useNarrow, usePolling, useTheme, type ThemePref } from './hooks'

export default function App() {
  const session = useAsync(async () => {
    try {
      return await api.me()
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null
      throw e
    }
  }, [])
  // undefined = follow the server session; null = signed out; User = signed in this tab.
  const [user, setUser] = useState<User | null | undefined>(undefined)
  // Whether this server runs the synthetic demo network; drives the banner. Real plant shows none.
  const [syntheticNow, setSynthetic] = useState<boolean | undefined>(undefined)
  if (user === undefined) {
    if (session.loading) return <Loading label="Starting…" />
    if (session.error) return <ErrorState error={session.error} onRetry={session.reload} />
  }
  const current = user === undefined ? session.data?.user ?? null : user
  if (!current)
    return (
      <Login
        onSignedIn={(u, synthetic) => {
          setSynthetic(synthetic)
          setUser(u)
        }}
      />
    )
  const synthetic = syntheticNow ?? session.data?.synthetic ?? false
  return <Workspace user={current} synthetic={synthetic} onSignOut={() => setUser(null)} />
}

function Workspace({ user, synthetic, onSignOut }: { user: User; synthetic: boolean; onSignOut: () => void }) {
  const theme = useTheme()
  const narrow = useNarrow()
  const net = useAsync(() => api.network(), [])
  const quality = useAsync(() => api.quality(), [])
  const incidents = useAsync(() => api.incidents(), [])
  usePolling(incidents.reload, 10_000)
  const [stack, setStack] = useState<View[]>([{ kind: 'overview' }])
  const [highlight, setHighlightState] = useState<MapHighlight>({})
  const [focus, setFocus] = useState<{ lngLat: LngLat; zoom?: number; bounds?: [LngLat, LngLat]; seq: number } | null>(null)
  const [tiers, setTiers] = useState<Record<CableTier, boolean>>({ backbone: true, feeder: true, distribution: true, drop: true })
  const [sheet, setSheet] = useState<'peek' | 'full'>('peek')
  const [edit, setEdit] = useState<EditState>({ kind: 'none' })
  const [toast, setToast] = useState<{ id: number; message: string; action?: { label: string; run: () => void | Promise<void> } } | null>(null)
  const notify = useCallback((message: string, action?: { label: string; run: () => void | Promise<void> }) => setToast({ id: Date.now(), message, action }), [])
  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast((x) => (x?.id === toast.id ? null : x)), 8000)
    return () => clearTimeout(t)
  }, [toast])

  const view = stack[stack.length - 1]!
  const open = useCallback((v: View) => {
    setStack((s) => (JSON.stringify(s[s.length - 1]) === JSON.stringify(v) ? s : [...s.slice(-19), v]))
  }, [])
  const back = useCallback(() => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s)), [])
  const replace = useCallback((v: View) => setStack((s) => [...s.slice(0, -1), v]), [])

  // Esc leaves any add/move mode; Backspace removes the last drawn cable point.
  const editRef = useRef(edit)
  editRef.current = edit
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      const cur = editRef.current
      if (e.key === 'Escape' && cur.kind !== 'none') {
        setEdit({ kind: 'none' })
        setStack((s) => (['add-node', 'add-cable'].includes(s[s.length - 1]!.kind) && s.length > 1 ? s.slice(0, -1) : s))
      }
      if (e.key === 'Backspace' && cur.kind === 'cable' && cur.fromNodeId && !cur.toNodeId && cur.via.length) {
        e.preventDefault()
        setEdit({ ...cur, via: cur.via.slice(0, -1) })
      }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])
  const setHighlight = useCallback((h: MapHighlight) => setHighlightState(h), [])
  const focusOn = useCallback((lngLat: LngLat, zoom?: number) => setFocus((f) => ({ lngLat, zoom, seq: (f?.seq ?? 0) + 1 })), [])
  const focusPath = useCallback((path: LngLat[]) => {
    if (!path.length) return
    const lngs = path.map((p) => p[0])
    const lats = path.map((p) => p[1])
    const bounds: [LngLat, LngLat] = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ]
    setFocus((f) => ({ lngLat: path[0]!, bounds, seq: (f?.seq ?? 0) + 1 }))
  }, [])
  const reloadNet = net.reload
  const reloadQuality = quality.reload
  const refreshNetwork = useCallback(() => {
    reloadNet()
    reloadQuality()
  }, [reloadNet, reloadQuality])

  const issueNodeIds = useMemo(() => new Set((quality.data?.issues ?? []).filter((i) => i.severity === 'high').flatMap((i) => (i.nodeId ? [i.nodeId] : []))), [quality.data])
  const selection: Selection = view.kind === 'node' ? { type: 'node', id: view.id } : view.kind === 'cable' ? { type: 'cable', id: view.id } : null

  if (!net.data) return net.error ? <ErrorState error={net.error} onRetry={net.reload} /> : <Loading label="Loading network…" />
  const network = net.data

  const ctx: AppCtx = {
    user,
    network,
    open,
    back,
    canGoBack: stack.length > 1,
    setHighlight,
    focusOn,
    focusPath,
    refreshNetwork,
    nodeName: (id) => network.nodes.find((n) => n.id === id)?.name ?? id,
    canEdit: user.permissions.includes('plant.edit'),
    canAck: user.permissions.includes('incident.ack'),
    incidents: incidents.data,
    reloadIncidents: incidents.reload,
    edit,
    setEdit,
    replace,
    notify,
  }

  /** Leave add/move mode and close an add form if it is showing. */
  const cancelEdit = () => {
    setEdit({ kind: 'none' })
    setStack((s) => (['add-node', 'add-cable'].includes(s[s.length - 1]!.kind) && s.length > 1 ? s.slice(0, -1) : s))
  }

  const nodeById = (id?: string) => network.nodes.find((n) => n.id === id)
  const startAdd = (k: AddableKind | 'cable' | 'import') => {
    if (k === 'import') {
      setEdit({ kind: 'none' })
      open({ kind: 'import' })
      setSheet('full')
      return
    }
    if (k === 'cable') {
      setEdit({ kind: 'cable', via: [] })
      open({ kind: 'add-cable' })
    } else {
      setEdit({ kind: 'place', nodeKind: k, picking: true })
      open({ kind: 'add-node' })
    }
    setSheet('peek')
  }

  // Map interaction and preview geometry for the current edit.
  let interaction: MapInteraction | undefined
  let draft: MapDraft | undefined
  if (edit.kind === 'place') {
    if (edit.picking) {
      interaction = {
        hint: `Tap the map where the new ${edit.nodeKind.toUpperCase()} is`,
        onClick: ({ lngLat }) => setEdit({ ...edit, location: lngLat, picking: false }),
        actions: [{ label: 'Cancel', onClick: cancelEdit }],
      }
    }
    draft = edit.location ? { points: [edit.location] } : undefined
  } else if (edit.kind === 'cable') {
    const from = nodeById(edit.fromNodeId)
    const to = nodeById(edit.toNodeId)
    if (!from) {
      interaction = {
        hint: 'Tap the START site of the new cable',
        onClick: ({ nodeId }) => nodeId && setEdit({ ...edit, fromNodeId: nodeId }),
        actions: [{ label: 'Cancel', onClick: cancelEdit }],
      }
    } else if (!to) {
      interaction = {
        hint: `From ${from.name}: tap bends along the route, then tap the END site`,
        onClick: ({ lngLat, nodeId }) => {
          if (nodeId && nodeId !== from.id) setEdit({ ...edit, toNodeId: nodeId })
          else if (!nodeId) setEdit({ ...edit, via: [...edit.via, lngLat] })
        },
        actions: [
          ...(edit.via.length ? [{ label: `Undo point (${edit.via.length})`, onClick: () => setEdit({ ...edit, via: edit.via.slice(0, -1) }) }] : []),
          { label: 'Change start', onClick: () => setEdit({ kind: 'cable', via: [] }) },
          { label: 'Cancel', onClick: cancelEdit },
        ],
      }
    } else {
      // Both ends chosen: stay in drawing mode so a stray tap cannot open another panel mid-save.
      interaction = {
        hint: `${from.name} → ${to.name}: check the details and save, or tap another site to change the end`,
        onClick: ({ nodeId }) => nodeId && nodeId !== from.id && setEdit({ ...edit, toNodeId: nodeId }),
        actions: [
          { label: 'Change end', onClick: () => setEdit({ ...edit, toNodeId: undefined }) },
          { label: 'Cancel', onClick: cancelEdit },
        ],
      }
    }
    draft = from ? { line: [from.location, ...edit.via, ...(to ? [to.location] : [])], points: edit.via } : undefined
  } else if (edit.kind === 'move') {
    const n = nodeById(edit.nodeId)
    if (!edit.to) interaction = { hint: `Tap the new position for ${n?.name ?? 'the site'}`, onClick: ({ lngLat }) => setEdit({ ...edit, to: lngLat }), actions: [{ label: 'Cancel', onClick: () => setEdit({ kind: 'none' }) }] }
    draft = edit.to ? { points: [edit.to] } : undefined
  }

  const onPick = (r: SearchResult) => {
    // Mid-edit, search is "take me there": move the map, keep the add form and the bar in place,
    // so a far end can be found by name without losing the cable being drawn.
    if (edit.kind !== 'none') {
      const n = r.type === 'cable' ? undefined : network.nodes.find((x) => x.id === (r.type === 'node' ? r.id : r.nodeId))
      const c = r.type === 'cable' ? network.cables.find((x) => x.id === r.id) : undefined
      if (n) focusOn(n.location, 18)
      else if (c) focusPath(c.path)
      notify(`Map moved to ${r.label} — still ${edit.kind === 'cable' ? 'drawing this cable' : 'placing this site'}`)
      return
    }
    if (r.type === 'node') {
      open({ kind: 'node', id: r.id })
      const n = network.nodes.find((x) => x.id === r.id)
      if (n) focusOn(n.location, 17)
    } else if (r.type === 'cable') {
      open({ kind: 'cable', id: r.id })
      const c = network.cables.find((x) => x.id === r.id)
      if (c) focusPath(c.path)
    } else {
      open({ kind: 'trace', serviceId: r.id })
      const n = network.nodes.find((x) => x.id === r.nodeId)
      if (n) focusOn(n.location, 17)
    }
    setSheet('peek')
  }

  const signOut = async () => {
    await api.logout().catch(() => undefined)
    onSignOut()
  }

  const issueCount = quality.data?.issues.filter((i) => i.severity === 'high').length ?? 0
  const outages = incidents.data?.incidents.filter((i) => i.kind !== 'drop').length ?? 0
  const nav: { id: View['kind']; label: string; icon: typeof Activity; view: View; badge?: number; show: boolean }[] = [
    { id: 'incidents', label: 'Incidents', icon: Siren, view: { kind: 'incidents' }, badge: outages, show: true },
    { id: 'overview', label: 'Overview', icon: LayoutDashboard, view: { kind: 'overview' }, show: true },
    { id: 'quality', label: 'Quality', icon: Activity, view: { kind: 'quality' }, badge: issueCount, show: true },
    { id: 'otdr', label: 'OTDR', icon: Radar, view: { kind: 'otdr' }, show: true },
    { id: 'audit', label: 'Audit', icon: History, view: { kind: 'audit' }, show: user.permissions.includes('audit.read') },
  ]
  const goRoot = (v: View) => {
    setStack([v])
    setSheet('peek')
  }

  const panel = renderView(view)

  return (
    <Ctx.Provider value={ctx}>
      <div className="flex h-full flex-col">
        {synthetic && (
          <div role="note" className="bg-warn-soft px-3 py-1 text-center text-xs font-medium text-warn">
            Synthetic demo network. Every DEMO- site and subscriber is fake; this is not the live EWS plant.
          </div>
        )}
        <IncidentBanner data={incidents.data} onOpen={(id) => goRoot(id ? { kind: 'incident', id } : { kind: 'incidents' })} />
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-surface px-3">
          <a href="/" className="flex shrink-0 items-center gap-2" aria-label="EWS Fiber Network Management System, home">
            <img src="/ewsfiber-logo.png" alt="" className="hidden h-11 w-auto sm:block" width={69} height={44} />
            <img src="/icon-192.png" alt="" className="h-8 w-8 sm:hidden" width={32} height={32} />
            <span className="hidden border-l border-border pl-2 text-xs font-semibold leading-tight text-muted lg:block">
              Network
              <br />
              Management
            </span>
          </a>
          <div className="flex min-w-0 flex-1 justify-center">
            <SearchBox onPick={onPick} />
          </div>
          {ctx.canEdit && <AddMenu onPick={startAdd} />}
          <ThemeSwitch pref={theme.pref} onChange={theme.setPref} />
          <UserMenu user={user} onSignOut={signOut} />
        </header>

        <div className="relative flex min-h-0 flex-1">
          {!narrow && (
            <nav aria-label="Main" className="flex w-20 shrink-0 flex-col items-stretch gap-1 border-r border-border bg-surface p-2">
              {nav
                .filter((n) => n.show)
                .map((n) => (
                  <NavButton key={n.id} active={stack[0]?.kind === n.id && stack.length === 1} onClick={() => goRoot(n.view)} icon={<n.icon className="h-5 w-5" aria-hidden />} label={n.label} badge={n.badge} />
                ))}
            </nav>
          )}

          <main className="relative min-w-0 flex-1">
            <MapView
              network={network}
              theme={theme.effective}
              selection={selection}
              highlight={highlight}
              tiers={tiers}
              issueNodeIds={issueNodeIds}
              focus={focus}
              interaction={interaction}
              draft={draft}
              onSelect={(s) => {
                if (!s) return
                open(s.type === 'node' ? { kind: 'node', id: s.id } : { kind: 'cable', id: s.id })
                setSheet('peek')
              }}
            />
            <div className={cx('pointer-events-none absolute left-3', narrow ? 'top-3' : 'bottom-6')}>
              <Legend network={network} tiers={tiers} defaultOpen={!narrow} onToggle={(t) => setTiers((x) => ({ ...x, [t]: !x[t] }))} />
            </div>
          </main>

          {!narrow && (
            <aside aria-label="Details" className="w-[420px] shrink-0 border-l border-border bg-surface">
              {panel}
            </aside>
          )}

          {narrow && (
            <aside aria-label="Details" className={cx('absolute inset-x-0 bottom-0 z-20 flex flex-col rounded-t-2xl border-t border-border bg-surface shadow-panel', sheet === 'full' ? 'h-[85%]' : 'h-[45%]')}>
              <button
                className="flex w-full items-center justify-center gap-1 py-1.5 text-xs text-muted"
                onClick={() => setSheet((s) => (s === 'full' ? 'peek' : 'full'))}
                aria-label={sheet === 'full' ? 'Shrink details panel' : 'Expand details panel'}
              >
                <span className="h-1 w-10 rounded-full bg-border" aria-hidden />
                {sheet === 'full' ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronUp className="h-4 w-4" aria-hidden />}
              </button>
              <div className="min-h-0 flex-1">{panel}</div>
            </aside>
          )}
        </div>

        {toast && (
          <div
            role="status"
            aria-live="polite"
            className={cx('fixed left-1/2 z-50 flex max-w-[92%] -translate-x-1/2 items-center gap-3 rounded-lg bg-[#111827] px-4 py-2.5 text-sm text-white shadow-panel', narrow ? 'bottom-20' : 'bottom-6')}
          >
            <span>{toast.message}</span>
            {toast.action && (
              <button
                className="rounded-md px-2 py-1 font-semibold text-[#93c5fd] hover:bg-white/10"
                onClick={async () => {
                  const a = toast.action!
                  setToast(null)
                  await a.run()
                }}
              >
                {toast.action.label}
              </button>
            )}
            <button className="text-white/70 hover:text-white" aria-label="Dismiss" onClick={() => setToast(null)}>
              ×
            </button>
          </div>
        )}

        {narrow && (
          <nav aria-label="Main" className="grid shrink-0 grid-cols-5 border-t border-border bg-surface pb-[env(safe-area-inset-bottom)]">
            {nav
              .filter((n) => n.show)
              .map((n) => (
                <NavButton key={n.id} active={stack[0]?.kind === n.id && stack.length === 1} onClick={() => goRoot(n.view)} icon={<n.icon className="h-5 w-5" aria-hidden />} label={n.label} badge={n.badge} />
              ))}
          </nav>
        )}
      </div>
    </Ctx.Provider>
  )
}

function renderView(view: View): ReactNode {
  switch (view.kind) {
    case 'overview':
      return <Overview />
    case 'node':
      return <NodePanel key={view.id} id={view.id} />
    case 'cable':
      return <CablePanel key={view.id} id={view.id} />
    case 'trace':
      return <TracePanel key={view.serviceId} serviceId={view.serviceId} />
    case 'impact':
      return <ImpactPanel key={JSON.stringify(view.target)} target={view.target} />
    case 'otdr':
      return <OtdrPanel key={JSON.stringify(view.from ?? null)} from={view.from} />
    case 'quality':
      return <QualityPanel />
    case 'audit':
      return <AuditPanel />
    case 'incidents':
      return <IncidentsPanel />
    case 'incident':
      return <IncidentPanel key={view.id} id={view.id} />
    case 'add-node':
      return <AddNodePanel />
    case 'port-path':
      return <PortPathPanel key={`${view.splitterId}:${view.port}`} splitterId={view.splitterId} port={view.port} nodeId={view.nodeId} />
    case 'add-cable':
      return <AddCablePanel />
    case 'import':
      return <ImportPanel />
  }
}

function AddMenu({ onPick }: { onPick: (k: AddableKind | 'cable' | 'import') => void }) {
  const [openMenu, setOpenMenu] = useState(false)
  // `verb: false` for entries that are not "Add <thing>", such as the importer.
  const items: { k: AddableKind | 'cable' | 'import'; label: string; sub: string; verb?: false }[] = [
    { k: 'nap', label: 'NAP', sub: 'Subscriber terminal box with splitter' },
    { k: 'lcp', label: 'LCP', sub: 'Primary splitter cabinet' },
    { k: 'closure', label: 'Closure', sub: 'Splice enclosure' },
    { k: 'cable', label: 'Cable', sub: 'Feeder, distribution or drop' },
    { k: 'pole', label: 'Pole', sub: 'Route support' },
    { k: 'handhole', label: 'Handhole', sub: 'Underground access' },
    { k: 'import', label: 'Import KML / KMZ', sub: 'As-built from Google Earth or a field app', verb: false },
  ]
  return (
    <div className="relative">
      <button
        onClick={() => setOpenMenu((o) => !o)}
        aria-expanded={openMenu}
        aria-haspopup="menu"
        className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-accent px-3 text-sm font-semibold text-accent-fg hover:opacity-90"
      >
        <Plus className="h-4 w-4" aria-hidden />
        <span className="hidden sm:inline">Add</span>
        <span className="sr-only sm:hidden">Add plant</span>
      </button>
      {openMenu && (
        <ul role="menu" className="absolute right-0 top-11 z-50 w-64 rounded-lg border border-border bg-surface py-1 shadow-panel" onMouseLeave={() => setOpenMenu(false)}>
          {items.map((i) => (
            <li key={i.k} role="none">
              <button
                role="menuitem"
                className="block w-full px-3 py-2 text-left hover:bg-surface-2"
                onClick={() => {
                  setOpenMenu(false)
                  onPick(i.k)
                }}
              >
                <span className="block text-sm font-medium">{i.verb === false ? i.label : `Add ${i.label}`}</span>
                <span className="block text-xs text-muted">{i.sub}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function NavButton({ active, onClick, icon, label, badge }: { active: boolean; onClick: () => void; icon: ReactNode; label: string; badge?: number }) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={cx('relative flex flex-col items-center gap-0.5 rounded-lg px-1 py-2 text-[11px] font-medium', active ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-surface-2 hover:text-fg')}
    >
      {icon}
      {label}
      {badge ? (
        <span className="num absolute right-2 top-1 min-w-[18px] rounded-full bg-danger px-1 text-center text-[10px] font-bold leading-[18px] text-accent-fg">
          {badge}
          <span className="sr-only"> issues</span>
        </span>
      ) : null}
    </button>
  )
}

function ThemeSwitch({ pref, onChange }: { pref: ThemePref; onChange: (p: ThemePref) => void }) {
  const next: Record<ThemePref, ThemePref> = { system: 'light', light: 'dark', dark: 'system' }
  const Icon = pref === 'light' ? Sun : pref === 'dark' ? Moon : Monitor
  return (
    <button onClick={() => onChange(next[pref])} className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-fg" aria-label={`Theme: ${pref}. Switch to ${next[pref]}.`} title={`Theme: ${pref}`}>
      <Icon className="h-5 w-5" aria-hidden />
    </button>
  )
}

function UserMenu({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <div className="hidden text-right leading-tight md:block">
        <div className="text-sm font-medium">{user.displayName}</div>
        <div className="text-xs text-muted">{ROLE_LABEL[user.role]}</div>
      </div>
      <button onClick={onSignOut} className="rounded-lg p-2 text-muted hover:bg-surface-2 hover:text-fg" aria-label="Sign out" title="Sign out">
        <LogOut className="h-5 w-5" aria-hidden />
      </button>
    </div>
  )
}
